import { Select3, type Select3Props } from "@el8/vital";
import type { HTMLAttributes } from "react";
import useEffectOnce from "../hooks/useEffectOnce";
import {
  useMediaDevices,
  useMicrophoneDeviceId,
  useScribeStore,
  useStep,
} from "../stores/scribe";

type Props = Omit<
  Select3Props<string, MediaDeviceInfo>,
  "items" | "onChange" | "value"
> & {
  onChange?: (value: string) => void;
};

/**
 * Select component to pick an audio input device for scribe. On mount, requests
 * microphone permission, creates the initial MediaStream (stored in scribe store),
 * and populates the device list. The persisted microphone choice is restored when present.
 */
export function AudioInputDeviceSelect({
  disabled,
  onChange,
  rootProps,
  ...props
}: Props): JSX.Element {
  const [, setStep] = useStep();
  const [microphoneDeviceId, setMicrophoneDeviceId] = useMicrophoneDeviceId();
  const [mediaDevices, getMediaDevices] = useMediaDevices();

  useEffectOnce(() => {
    const getInitialMicrophoneDeviceId = async (): Promise<void> => {
      try {
        const devices = await getMediaDevices();

        if (!devices.length) return;

        const current = useScribeStore.getState().media.microphoneDeviceId;
        const found = current && devices.some((d) => d.deviceId === current);

        if (!found) setMicrophoneDeviceId(devices[0].deviceId);
      } catch {
        setStep("errorMicrophoneInitial");
      }
    };

    getInitialMicrophoneDeviceId();
  });

  const handleChange = (deviceId: string): void => {
    setMicrophoneDeviceId(deviceId);
    onChange?.(deviceId);
  };

  const items = mediaDevices ?? [];

  return (
    <Select3<string, MediaDeviceInfo>
      disabled={disabled}
      getItemKey={(item) => item.deviceId}
      getItemLabel={(item) => item.label}
      getItemValue={(item) => item.deviceId}
      inlineDropdown
      items={items}
      multiline
      name="audioInputDevice"
      onChange={handleChange}
      placeholder="----"
      value={microphoneDeviceId ?? undefined}
      rootProps={
        {
          "data-dd-privacy": "allow", // Avoid masking the options in the Microphone Select
          ...rootProps,
        } as unknown as HTMLAttributes<HTMLDivElement>
      }
      {...props}
    />
  );
}
